import { DocumentationEntry } from '../interfaces/documentation.interface'

/**
 * Catálogo de la documentación interna. Es la ÚNICA fuente: de aquí salen las rutas
 * (`/documentation/<slug>`) y los items del sidebar (`menu.constant.ts` lo importa), así
 * que no pueden desincronizarse.
 *
 * Los documentos van en pares: un `manual` explica cómo funciona el negocio y su `guia`
 * explica cómo se hace eso en el panel. **Ambos usan los mismos `id` de sección**, que es
 * lo que permite conmutar entre teoría y práctica sin perder el lugar. Sólo los manuales
 * aparecen en el sidebar; a las guías se llega con el conmutador de la cabecera.
 *
 * Agregar un documento = un archivo en `src/assets/docs/` + una entrada aquí. El backend
 * no se entera: no conoce el catálogo, sólo valida la forma del slug.
 * Ver `src/assets/docs/README.md`.
 */
export const DOCUMENTATION_CATALOG: DocumentationEntry[] = [
  {
    slug: 'manual-contable-saldos',
    title: 'Manual contable de saldos',
    menuLabel: 'Manual contable',
    subtitle: 'Arrendamiento puro · del alta del activo al castigo de cartera',
    category: 'Contabilidad',
    icon: 'fa-solid fa-book',
    asset: 'assets/docs/manual-contable-saldos.html',
    version: '1.1',
    updatedAt: '2026-09-09',
    source: 'EJEMPLO DEL ARRENDAMIENTO PURO.xlsx',
    available: true,
    kind: 'manual',
    toggleLabel: 'El manual',
  },
  {
    slug: 'manual-pagos-cartera',
    title: 'Manual de pagos y cartera',
    menuLabel: 'Manual de pagos',
    subtitle: 'Cómo entra el dinero, cómo se aplica y cómo baja la cartera',
    category: 'Contabilidad',
    icon: 'fa-solid fa-hand-holding-dollar',
    asset: 'assets/docs/manual-pagos-cartera.html',
    version: '1.2',
    updatedAt: '2026-09-09',
    source: 'Módulos de pagos y cartera de la plataforma',
    available: true,
    kind: 'manual',
    companion: 'guia-pagos-plataforma',
    toggleLabel: 'El manual',
  },
  {
    slug: 'guia-pagos-plataforma',
    title: 'Pagos · en la plataforma',
    menuLabel: 'Pagos en la plataforma',
    subtitle: 'Dónde se hace cada cosa del manual de pagos, pantalla por pantalla',
    category: 'Contabilidad',
    icon: 'fa-solid fa-mouse-pointer',
    asset: 'assets/docs/guia-pagos-plataforma.html',
    version: '1.1',
    updatedAt: '2026-09-09',
    available: true,
    kind: 'guia',
    companion: 'manual-pagos-cartera',
    toggleLabel: 'En la plataforma',
  },
  {
    slug: 'manual-facturas',
    title: 'Manual de facturas',
    menuLabel: 'Manual de facturas',
    subtitle: 'Del layout de emisión al complemento de pago',
    category: 'Contabilidad',
    icon: 'fa-solid fa-file-invoice',
    asset: 'assets/docs/manual-facturas.html',
    version: '1.1',
    updatedAt: '2026-09-09',
    source: 'Módulo de facturas de la plataforma',
    available: true,
    kind: 'manual',
    companion: 'guia-facturas-plataforma',
    toggleLabel: 'El manual',
  },
  {
    slug: 'guia-facturas-plataforma',
    title: 'Facturas · en la plataforma',
    menuLabel: 'Facturas en la plataforma',
    subtitle: 'Dónde se hace cada cosa del manual de facturas, pantalla por pantalla',
    category: 'Contabilidad',
    icon: 'fa-solid fa-mouse-pointer',
    asset: 'assets/docs/guia-facturas-plataforma.html',
    version: '1.1',
    updatedAt: '2026-09-09',
    available: true,
    kind: 'guia',
    companion: 'manual-facturas',
    toggleLabel: 'En la plataforma',
  },
  {
    //* El par manual/guía comparte los `id` de sección: es lo que deja al conmutador de la
    //* cabecera saltar entre el concepto y la práctica sin perder el lugar. Si se renombra una
    //* sección en uno, hay que renombrarla en el otro o el salto aterriza en el índice.
    slug: 'manual-syntage',
    title: 'Análisis fiscal (Syntage · Ekatena)',
    menuLabel: 'Análisis fiscal',
    subtitle: 'De la CIEC del cliente a las dos ofertas, de dónde salen los datos y qué hacer cuando algo no sale',
    category: 'Originación',
    icon: 'fa-solid fa-file-invoice-dollar',
    asset: 'assets/docs/manual-syntage.html',
    version: '1.3',
    updatedAt: '2026-10-29',
    source: 'Módulos de verificación y algoritmo de ofertas',
    available: true,
    kind: 'manual',
    companion: 'guia-syntage-plataforma',
    toggleLabel: 'El manual',
  },
  {
    slug: 'guia-syntage-plataforma',
    title: 'Análisis fiscal · en la plataforma',
    menuLabel: 'Análisis fiscal en la plataforma',
    subtitle: 'Dónde se hace cada cosa del manual, pantalla por pantalla',
    category: 'Originación',
    icon: 'fa-solid fa-mouse-pointer',
    asset: 'assets/docs/guia-syntage-plataforma.html',
    version: '1.2',
    updatedAt: '2026-08-26',
    available: true,
    kind: 'guia',
    companion: 'manual-syntage',
    toggleLabel: 'En la plataforma',
  },
]

export const findDocumentation = (slug: string): DocumentationEntry | undefined => DOCUMENTATION_CATALOG.find((doc) => doc.slug === slug)

/** Lo que se lista en el sidebar: los manuales. A las guías se llega con el conmutador. */
export const DOCUMENTATION_MENU = DOCUMENTATION_CATALOG.filter((doc) => doc.available && doc.kind === 'manual')
